import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Показываем сообщение об ошибке из state (если был редирект из ProtectedRoute)
  useEffect(() => {
    const state = location.state as { error?: string } | null;
    if (state?.error) {
      toast.error(state.error);
      // Очищаем state, чтобы сообщение не показывалось при следующей загрузке
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Заполните все поля');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      
      // Поддерживаем оба формата ответа: {token, user} и {access_token, user}
      const token = response.data.access_token || response.data.token;
      const user = response.data.user;
      
      if (token && user) {
        // Проверка роли перед сохранением токена
        const userRole = user.role_name || user.role;
        if (userRole !== 'admin' && userRole !== 'manager') {
          toast.error('Доступ разрешен только администраторам и менеджерам');
          setLoading(false);
          return;
        }
        
        // Сохраняем токен для админки
        localStorage.setItem('admin_token', token);
        localStorage.setItem('admin_user', JSON.stringify(user));
        // Также сохраняем токен для основного сайта (синхронизация)
        localStorage.setItem('token', token);
        localStorage.setItem('access_token', token);
        localStorage.setItem('user', JSON.stringify(user));
        toast.success('Успешный вход');
        navigate('/');
      } else if (response.data.success) {
        // Старый формат ответа
        const oldToken = response.data.token;
        const oldUser = response.data.user;
        
        // Проверка роли перед сохранением токена
        const userRole = oldUser.role_name || oldUser.role;
        if (userRole !== 'admin' && userRole !== 'manager') {
          toast.error('Доступ разрешен только администраторам и менеджерам');
          setLoading(false);
          return;
        }
        
        localStorage.setItem('admin_token', oldToken);
        localStorage.setItem('admin_user', JSON.stringify(oldUser));
        // Также сохраняем токен для основного сайта (синхронизация)
        localStorage.setItem('token', oldToken);
        localStorage.setItem('access_token', oldToken);
        localStorage.setItem('user', JSON.stringify(oldUser));
        toast.success('Успешный вход');
        navigate('/');
      } else {
        toast.error(response.data.message || 'Ошибка входа');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.response?.data?.detail || 'Ошибка входа в систему');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <FileText className="h-8 w-8 text-blue-600" />
            <span className="text-xl font-bold text-gray-900">РаботаМакс Admin</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Вход в админ-панель</h2>
          <p className="text-gray-600">Войдите с правами администратора или менеджера</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="admin@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Пароль
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Вход...' : 'Войти'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;

